import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import CustomTextarea, { CustomFileUpload, CustomInput } from '../Components/CustomFormElements';
import {
  InformationPackageApiProvider,
  useInformationPackageApi,
} from '../Services/InformationPackageApiProvider';
import { removeExtension } from '../utils/removeFileExtension';

const defaultFormats = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
  'text/plain', // txt
  'text/markdown', // md
  'text/csv',
  'text/html',
];

const allowedExtensions = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'md', 'csv', 'html'];

const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

function NewInformationPackageContent({ canEdit }) {
  const navigate = useNavigate();

  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { createInformationPackage } = useInformationPackageApi();

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    files: [],
    link: '',
    isNameAuto: true,
  });

  const onLinkChange = (id, value) => {
    setForm((prev) => ({
      ...prev,
      link: value,
      name:
        prev.isNameAuto && prev.files.length === 0 ? value.replace(/^https?:\/\//, '') : prev.name,
    }));
  };

  const onFileSelect = (selectedFiles) => {
    setForm((prev) => ({
      ...prev,
      files: selectedFiles,
      name: prev.isNameAuto ? removeExtension(selectedFiles[0]?.name ?? '') : prev.name,
    }));
  };

  const onCreate = async () => {
    if (!form.name.trim()) {
      showAlert('fail', 'Information package name is required');
      return;
    }

    const link = form.link.trim();

    if (form.files.length === 0 && !link) {
      showAlert('fail', 'Please provide either a file or a link.');
      return;
    }

    if (link && !/^(https?:\/\/)([\w.-]+)\.([a-z]{2,6})(\/[^\s]*)?\/?$/.test(link)) {
      showAlert('fail', 'Invalid link format. Please enter a valid URL.');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      formData.append('name', form.name);
      formData.append('description', form.description);

      form.files.forEach((file) => {
        formData.append('files', file);
      });

      if (link) {
        formData.append('links', link);
      }

      await createInformationPackage(formData);
      showAlert('success', 'Information package created successfully!');
      navigate(`/organizations/${organizationId}/information-package`);
    } catch (error) {
      showAlert('fail', 'Failed to create information package');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/information-package`);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{form.name}</h2>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="information-package-name"
                label="Information Package Name"
                placeholder="Enter information package name"
                type="text"
                value={form.name}
                onChange={(id, value) => setForm({ ...form, name: value, isNameAuto: false })}
                //disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id="description"
                label="Description"
                name="description"
                type="text"
                addedText={form.description}
                onChange={(value) => setForm({ ...form, description: value })}
                resizable={false}
                //disabled={isDisabled}
                height="200px"
              />
            </div>
          </div>

          <div className="input-block d-flex flex-column justify-between">
            <div className="mb-2">
              <div className="input-title">Information Package unit</div>
              <div className="small-font">Select or replace information package unit</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomFileUpload
                multiple={true}
                maxFileSize={MAX_SIZE_BYTES}
                allowedFormats={defaultFormats}
                allowedExtensions={allowedExtensions}
                showUploadedFiles={true}
                allowFileDeletion={true}
                onFileSelect={onFileSelect}
                showAlert={showAlert}
                showConfirmation={showConfirmation}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-column justify-between">
            <div className="mb-2">
              <div className="input-title">Link to site</div>
              <div className="small-font">Enter URL</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="information-package-link"
                label="Link"
                placeholder="Enter site link..."
                type="text"
                value={form.link}
                onChange={onLinkChange}
              />
            </div>
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" onClick={onCreate}>
              Create
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewInformationPackage({ canEdit }) {
  return (
    <ConfirmationProvider>
      <InformationPackageApiProvider>
        <NewInformationPackageContent canEdit={canEdit} />
      </InformationPackageApiProvider>
    </ConfirmationProvider>
  );
}

export default NewInformationPackage;
